import logo from "@/assets/p2pro-logo.webp";
import { Zap, ShieldCheck, Tv, TrendingUp, Users, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { memo } from "react";

const STATS = [
  { id: 1, n: "+8 mil", l: "revendedores", icon: Users },
  { id: 2, n: "99,9%", l: "uptime", icon: TrendingUp },
  { id: 3, n: "24/7", l: "suporte", icon: Clock },
] as const;

const Stat = memo(({ n, l, icon: Icon }: typeof STATS[number]) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-start"
  >
    <div className="flex items-center gap-2 mb-1">
      <div className="text-2xl font-black text-electric tabular-nums">{n}</div>
      <Icon className="w-4 h-4 text-electric/60" aria-hidden="true" />
    </div>
    <div className="text-[10px] md:text-xs text-muted-foreground uppercase tracking-widest font-semibold">{l}</div>
  </motion.div>
));

Stat.displayName = "Stat";

const FloatingBadge = memo(({ icon, text, className }: { icon: React.ReactNode; text: string; className?: string }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.2, ease: "easeOut" }}
    className={`absolute ${className} bg-card/60 backdrop-blur-xl border border-white/5 rounded-2xl px-5 py-3.5 flex items-center gap-3 text-sm font-bold shadow-2xl animate-float whitespace-nowrap hidden sm:flex pointer-events-none`}
  >
    <span className="text-electric drop-shadow-[0_0_8px_oklch(0.78_0.2_220)]" aria-hidden="true">{icon}</span>
    {text}
  </motion.div>
));

FloatingBadge.displayName = "FloatingBadge";

export function Hero() {
  const whatsappUrl = `https://api.whatsapp.com/send?phone=5583986147064&text=${encodeURIComponent("Olá! Vim do site p2propaineliptv.com.br, gostaria de mais informações.")}`;

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-hero pt-16 md:pt-0">
      {/* Decoupled layer for background effects to minimize CLS and heavy painting during text interaction */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute top-1/4 -left-20 w-[40vw] h-[40vw] rounded-full bg-electric/10 blur-[120px] animate-pulse-glow will-change-[opacity,transform]" />
        <div className="absolute bottom-1/4 -right-20 w-[40vw] h-[40vw] rounded-full bg-primary/10 blur-[120px] animate-pulse-glow will-change-[opacity,transform]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12 pb-24 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 1, x: 0 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "circOut" }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 text-white text-[11px] font-black uppercase tracking-[0.2em] mb-10 shadow-sm">
            <Zap className="w-3.5 h-3.5" aria-hidden="true" /> 
            <span>Programa Oficial</span>
          </div>
          
          <h1 className="text-fluid-h1 font-black leading-[0.8] tracking-[-0.05em] text-balance uppercase italic">
            O Melhor <span className="text-electric">Painel IPTV</span> <br />
            Pós Pago do Brasil
          </h1>
          
          <p className="mt-12 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed text-balance tracking-tight">
            A infraestrutura definitiva para revendedores de elite. Tenha seu <strong className="text-foreground font-bold">Painel IPTV Pós Pago</strong> com créditos sob demanda e mais de 120.000 títulos em 4K.
          </p>
          
          <div className="mt-12 flex flex-wrap gap-5">
            <motion.a 
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.98 }}
              href={whatsappUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-electric text-black px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:shadow-glow transition-all active:scale-95 flex items-center gap-3 min-h-[56px]"
            >
              Começar Agora <span className="text-xl">→</span>
            </motion.a>
            <motion.a 
              whileHover={{ backgroundColor: "rgba(255,255,255,0.05)", borderColor: "var(--electric)" }}
              href="#recursos" 
              className="px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-[0.2em] border border-white/10 bg-white/5 backdrop-blur-md transition-all flex items-center"
            >
              Conhecer Painel
            </motion.a>
          </div>
          
          <div className="mt-16 pt-8 border-t border-white/5 grid grid-cols-3 gap-6 md:gap-12">
            {STATS.map(stat => (
              <Stat key={stat.id} {...stat} />
            ))}
          </div>
        </motion.div>

        <div className="relative flex justify-center lg:justify-end mt-12 lg:mt-0">
          <div className="relative z-10 w-full max-w-[500px] aspect-[4/5] rounded-[2rem] sm:rounded-[2.5rem] bg-gradient-to-b from-white/[0.05] to-transparent border border-white/[0.08] backdrop-blur-2xl p-8 sm:p-12 flex flex-col items-center justify-center shadow-2xl">
             <div className="text-center w-full relative">
                <div className="text-[10rem] sm:text-[14rem] font-black text-electric absolute -top-16 -left-12 select-none pointer-events-none italic opacity-20 z-0">4K</div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="relative z-10"
                >
                  <div className="text-7xl sm:text-9xl font-black text-white italic drop-shadow-glow leading-none">4K</div>
                  <p className="text-xl sm:text-2xl font-black uppercase tracking-[0.4em] text-electric mt-2">Ultra HD</p>
                </motion.div>

                <div className="mt-12 flex flex-col gap-4 relative z-10">
                   <div className="h-1.5 w-full max-w-[200px] bg-white/10 rounded-full mx-auto overflow-hidden">
                      <motion.div 
                        initial={{ x: "-100%" }}
                        animate={{ x: "100%" }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="h-full w-full bg-electric"
                      />
                   </div>
                   <div className="flex flex-col gap-1">
                     <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white">Transmissão Pura</span>
                     <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 italic">Sem Compressão Digital</span>
                   </div>
                </div>
             </div>
            
            <FloatingBadge 
              icon={<ShieldCheck className="w-5 h-5" />} 
              text="Sistema Anti-Quedas" 
              className="-top-4 -left-8" 
            />
            <FloatingBadge 
              icon={<Tv className="w-5 h-5" />} 
              text="Canais & VOD 2026" 
              className="bottom-12 -right-4" 
            />
          </div>
          
          <div className="absolute inset-0 bg-electric/5 blur-[140px] rounded-full animate-pulse-glow z-0 pointer-events-none" />
        </div>
      </div>
    </section>
  );
}